import * as React from 'react';
import { InvitesTableProps } from "./invites-table.types";
import { NewTableColumn, NewTableColumnAction, NewTableColumnPredefinedActionType, NewTableHeaderAction, NewTableHeaderPredefinedActionType } from '@common/components/new-table';
import { InviteListDto } from '@common/dto/invite/list-dto';
import { I18n } from 'react-redux-i18n';
import ActionsButton, { TRIGGER_TYPE } from '@common/components/ActionsButton';
import { useTableController } from '@common/hooks/table';
import { HttpErrorDto } from '@common/dto/http';
import { AdminSmartListFunctionType } from '@common/dto/admin-function/type';
import { InfoMessage } from '@common/components/info-message';
import { IndividualListDto } from '@common/dto/individual/list-dto';
import { QuestionnaireItemMapDto } from '@common/dto/questionnaire/questionnaire-item-map-dto';
import { getCurrentUrl } from '@common/utils/url';
import { getTestAttributes } from '@common/utils/tests';
import { InviteStatus } from '@common/dto/invite-status';
import { MODULE_NAME } from '@modules/invite-status/constants/shared';
import { InviteReviewedStatusStateUpdateDto } from '@common/dto/invite-reviewed-status-state/update-dto';
import { SendInviteParams } from '@common/actions/individuals';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { QuestionnaireGetDto } from '@common/dto/questionnaire/get-dto';
import uniq from 'lodash/uniq';

export const useController = (props: InvitesTableProps) => {
  const {
    data,
    getQuestionnaires,
    showInfoMessage,
    filters,
    columns,
    selectedItems,
    getData: getDataBase,
    getColumnId,
    onRestoreColumnsToDefault,
    showErrorMessage,
    adminFunctions,
    showMessage,
    closeMessage,
    sendInvites,
    onOpenInviteQuestionnaire,
    generateLetter,
    openPedigree,
    openDatasheet,
    getReviewedStatusState,
    updateReviewedStatusActions,
    updateInviteStatus,
    onLaunchNewQuestionnaire,
    getQuestionnairesForNewQuestionnaire,
    createBatchLetters,
    updateInvitesLockStatus,
    validateIndividualEmail,
    getQuestionnaireForInvite,
  } = props;
  
  const {
    setChangingColumns,
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    handleSetCurrentSelectedRow,
    handleColumnsResizing,
    handleColumnsOrderChange,
  } = useTableController(props, { withFirstActionsColumn: true });

  const handleRestoreColumnsToDefault = React.useCallback(async () => {
    try {
      setChangingColumns(true);
      await onRestoreColumnsToDefault();
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to restore column to default'));
    } finally {
      setChangingColumns(false);
    }
  }, [onRestoreColumnsToDefault, showErrorMessage, setChangingColumns]);

  const handleLockAndUnlockInvite = React.useCallback((inviteId: InviteListDto['id'], invite: InviteListDto) => {
    const { fhq4, locked } = invite;
    const lock = locked !== "true";

    const lockMessage = `${I18n.t('After locking this invite the patient will not be able to edit the questionnaire')}. ${I18n.t('Are you sure you want to lock this invite? You can unlock it again later if needed')}`;
    const unlockMessage = `${I18n.t('If you unlock this invite the patient will be able to access the questionnaire and modify data')}. ${I18n.t('Are you sure you want to unlock?')}`;

    const message = lock ? lockMessage : unlockMessage; 
    
    showMessage({
      header: I18n.t('Confirmation Message'),
      confirmText: I18n.t('Yes'),
      size: 'medium',
      cancelText: I18n.t('No'),
      content: <InfoMessage>{message}</InfoMessage>,
      onCustomOk: async () => {
        try {
          await updateInvitesLockStatus([
            {
              inviteId: invite.id,
              lock,
              fhq4,
            }
          ]);
          closeMessage();
        } catch(err) {
          console.error(err);
    
          const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to save lock/unlock invite');
          showErrorMessage(errorMessage);
        }
      }
    });
  }, [showMessage, updateInvitesLockStatus, closeMessage, showErrorMessage]);

  const [newQuestionnaireProps, setNewQuestionnaireProps] = React.useState<{ name: string; id: string; familyId: string; individualName: string } | undefined>();

  const handleNewQuestionnaireOpen = React.useCallback((id: string, item: InviteListDto) => {
    setNewQuestionnaireProps({
      id: item.id,
      name: item.name,
      familyId: item.familyId,
      individualName: item.individualName
    });
  }, []);

  const closeNewQuestionnaire = React.useCallback(() => {
    setNewQuestionnaireProps(undefined);
  }, []);

  const handleNewQuestionnaire = React.useCallback(async (questionnaire: QuestionnaireListDto | undefined) => {
    if (!newQuestionnaireProps) {
      return;
    }

    await onLaunchNewQuestionnaire(newQuestionnaireProps.id, questionnaire);
    closeNewQuestionnaire();
  }, [onLaunchNewQuestionnaire, newQuestionnaireProps, closeNewQuestionnaire]);

  const handleGetQuestionnairesForNewQuestionnaire = React.useCallback(async () => {
    if (!newQuestionnaireProps) {
      return [];
    }

    const res = await getQuestionnairesForNewQuestionnaire(newQuestionnaireProps.familyId);

    return res;
  }, [getQuestionnairesForNewQuestionnaire, newQuestionnaireProps?.familyId]);

  const [viewInviteHistoryProps, setViewInviteHistoryProps] = React.useState<{ invite: InviteListDto; individual: { id: IndividualListDto['id']; name: string } }>();

  const openViewInviteHistoryModal = React.useCallback((inviteId: InviteListDto['id'], invite: InviteListDto) => {
    setViewInviteHistoryProps({
      invite,
      individual: {
        name: invite.individualName,
        id: invite.globalId
      }
    });
  }, []);

  const closeViewInviteHistoryModal = React.useCallback(() => {
    setViewInviteHistoryProps(undefined);
  }, []);

  const [openQuestionnaireProps, setOpenQuestionnaireProps] = React.useState<{ individualId: InviteListDto['globalId']; inviteId: InviteListDto['id']; individualName: string; } | undefined>();

  const handleOpenQuestionnaireOpen = React.useCallback((id: InviteListDto['id'], item: InviteListDto) => {
    setOpenQuestionnaireProps({
      individualId: item.globalId,
      inviteId: item.id,
      individualName: item.individualName,
    });
  }, []);

  const closeOpenQuestionnaire = React.useCallback(() => {
    setOpenQuestionnaireProps(undefined);
  }, []);

  const handleOpenQuestionnaire = React.useCallback(async (questionnaire: QuestionnaireItemMapDto) => {
    if (!openQuestionnaireProps) {
      return;
    }

    const currentUrl = getCurrentUrl();
    const { url } = await onOpenInviteQuestionnaire(questionnaire, currentUrl);

    window.location.href = url;
    closeOpenQuestionnaire();
  }, [onOpenInviteQuestionnaire, openQuestionnaireProps, closeOpenQuestionnaire]);

  const [newInviteProps, setNewInviteProps] = React.useState<{ inviteId: InviteListDto['id']; individualId: IndividualListDto['id']; } | undefined>();

  const handleNewInviteOpen = React.useCallback((id: InviteListDto['id'], item: InviteListDto) => {
    setNewInviteProps({
      individualId: item.globalId,
      inviteId: item.id,
    });
  }, []);

  const closeNewInvite = React.useCallback(() => {
    setNewInviteProps(undefined);
  }, []);

  const handleNewInviteSend = React.useCallback(async (data: SendInviteParams) => {
    if (!newInviteProps) {
      return;
    }

    await sendInvites([data]);
    closeNewInvite();
    showInfoMessage(I18n.t('The Questionnaire invitation emails were successfully submitted'));
  }, [newInviteProps, closeNewInvite, sendInvites]);

  const [sendReminderProps, setSendReminderProps] = React.useState<{ fhq4: boolean; inviteId: InviteListDto['id']; individualId: IndividualListDto['id']; } | undefined>();

  const handleSendReminderOpen = React.useCallback((id: InviteListDto['id'], item: InviteListDto) => {
    if (item.locked === 'true') {
      showErrorMessage(I18n.t('inviteIsLockedToSendReminder'));

      return;
    }

    let fhq4 = false;
    try {
      fhq4 = JSON.parse(item.fhq4);
    } catch {}

    setSendReminderProps({
      fhq4,
      individualId: item.globalId,
      inviteId: item.id,
    });
  }, [showErrorMessage]);

  const closeSendReminder = React.useCallback(() => {
    setSendReminderProps(undefined);
  }, []);

  const handleSendReminderSend = React.useCallback(async (data: SendInviteParams) => {
    if (!sendReminderProps) {
      return;
    }

    await sendInvites([data]);
    closeSendReminder();
    showInfoMessage(I18n.t('The Questionnaire reminder email was successfully sent'))
  }, [sendReminderProps, closeSendReminder, sendInvites]);

  const handleUpdateInvitesStatus = React.useCallback(async (items: Array<InviteListDto>, status: InviteStatus) => {
    try {
      await updateInviteStatus({
        inviteIds: items.map((x) => ({
          id: Number(x.id), fhq4: JSON.parse(x.fhq4)
        })),
        status
      });
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to update status'));
    }
  }, [updateInviteStatus, showErrorMessage]);

  const [updateStatusProps, setUpdateStatusProps] = React.useState<{ id: InviteListDto['id']; fhq4: boolean }>();

  const closeUpdateReviewedStatusModal = React.useCallback(() => {
    setUpdateStatusProps(undefined);
  }, []);

  const handleUpdateReviewedStatusSubmit = React.useCallback(async (data: InviteReviewedStatusStateUpdateDto) => { 
    await updateReviewedStatusActions(data);
    closeUpdateReviewedStatusModal();
  }, [updateReviewedStatusActions, closeUpdateReviewedStatusModal]);

  const handleOpenUpdateReviewedStatusModal = React.useCallback(
    (item: InviteListDto) => async () => {
      try {
        const data = await getReviewedStatusState(item.id, JSON.parse(item.fhq4));

        if (data.moveToFolder || data.sendEmail || data.transmitPedigreeToEMR) {
          setUpdateStatusProps({
            id: item.id,
            fhq4: JSON.parse(item.fhq4),
          });
        } else {
          handleUpdateInvitesStatus([item], InviteStatus.reviewed);
        }
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to get status data"));
      }
    },
    [handleUpdateInvitesStatus, showErrorMessage, getReviewedStatusState]
  );

  const handleOpenPedigree = React.useCallback((id: InviteListDto['id'], invite: InviteListDto) => {
    openPedigree(invite.globalId);
  }, [openPedigree]);

  const handleOpenDatasheet = React.useCallback((id: InviteListDto['id'], invite: InviteListDto) => {
    openDatasheet(invite.globalId);
  }, [openDatasheet]);

  const handleGenerateLetter = React.useCallback((id: InviteListDto['id'], invite: InviteListDto) => {
    generateLetter(invite.globalId);
  }, [generateLetter]);

  const getUpdateStatusOptions = (item: InviteListDto) => {
    return [
      { label: 'Completed Manually', onClick: () => handleUpdateInvitesStatus([item], InviteStatus.completeManually) },
      { label: 'Inactive', onClick: () => handleUpdateInvitesStatus([item], InviteStatus.inactive) },
      { label: 'Reviewed', onClick: handleOpenUpdateReviewedStatusModal(item) }
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  };

  const handleBatchLetters = React.useCallback(() => {
    createBatchLetters(selectedItems.map((x) => x.globalId));
  }, [createBatchLetters, selectedItems]);

  const handleUnlockAndLockInvites = React.useCallback((lock: boolean) => {
    const message = lock ? I18n.t('lockInvitesMsg') : I18n.t('unlockInvitesMsg');

    showMessage({
      header: I18n.t('Confirmation Message'),
      confirmText: I18n.t('Yes'),
      cancelText: I18n.t('No'),
      size: 'medium',
      content: <InfoMessage>{message}</InfoMessage>,
      onCustomOk: async () => {
        try {
          const dataToSend = selectedItems.map((x) => ({
            inviteId: x.id,
            lock,
            fhq4: x.fhq4,
          }));
          await updateInvitesLockStatus(dataToSend);
          closeMessage();
        } catch(err) {
          console.error(err);
    
          const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to save lock/unlock invite');
          showErrorMessage(errorMessage);
        }
      }
    });
  }, [showMessage, updateInvitesLockStatus, closeMessage, showErrorMessage, selectedItems]);

  const handleUnlockInvites = React.useCallback(() => {
    handleUnlockAndLockInvites(false);
  }, [handleUnlockAndLockInvites]);

  const handleLockInvites = React.useCallback(() => {
    handleUnlockAndLockInvites(true);
  }, [handleUnlockAndLockInvites]);

  const [batchNewInvitesProps, setBatchNewInvitesProps] = React.useState<{ opened: boolean }>({ opened: false });

  const handleBatchNewInvitesOpen = React.useCallback(() => {
    setBatchNewInvitesProps({ opened: true });
  }, []);

  const closeBatchNewInvites = React.useCallback(() => {
    setBatchNewInvitesProps({ opened: false });
  }, []);

  const validateSelectedItemsEmail = async (newInvite: SendInviteParams) => {
    const emailsValidationResponses = await Promise.all(selectedItems.map(async ({ globalId, id, }) => {
      const response = await validateIndividualEmail({
        individualId: globalId,
        fhq4: newInvite.fhq4,
        questionnaireId: newInvite.questionnaireId,
      });

      return {
        inviteId: id, 
        globalId,
        response
      };
    }));
    const invitesWithError = emailsValidationResponses
      .filter((x) => x.response.code !== 200)
      .reduce<Array<InviteListDto>>((acc, x) => {
        const invite = data.find(({ id }) => id === x.inviteId);

        if (invite) {
          acc.push(invite);
        }

        return acc;
      }, []);

    if (invitesWithError.length) {
      throw {
        entity: (
          <div>
            <p>
              {I18n.t('There is a problem with the email addresses for the selected invites')}. {I18n.t('Invitations were not sent to the following individuals:')}
            </p>
            <p>
              {invitesWithError.map((x) => <p key={x.id}>{x.name}</p>)}
            </p>
            <p>
            {I18n.t('Please enter valid email addresses and resend the invitations')}
            </p>
          </div>
        )
      };
    }
  };

  const handleBatchNewInvitesSend = async (newInvite: SendInviteParams) => {
    await validateSelectedItemsEmail(newInvite);
    await sendInvites(selectedItems.map(({ globalId }) => {
      const copy = { ...newInvite };
      copy.globalId = globalId;

      return copy;
    }));
    
    showInfoMessage(I18n.t('The Questionnaire invitation emails were successfully submitted'));
    closeBatchNewInvites();
  };

  const getQuestionnairesForBatchInvites = React.useCallback(() => {
    return getQuestionnaires({
      includeFamilyQuestionnaires: false,
      includeFhq4Questionnaires: true,
      includeHiddenQuestionnaires: false
    });
  }, [getQuestionnaires]);

  const [batchSendReminderProps, setBatchSendReminderProps] = React.useState<{ questionnaire: QuestionnaireGetDto } | undefined>();

  const handleBatchSendReminderOpen = React.useCallback(async () => {
    let questionnaires: Array<QuestionnaireGetDto> = [];

    try {
      questionnaires = await Promise.all(
        selectedItems.map((x) => {
          let fhq4 = false;
          try {
            fhq4 = JSON.parse(x.fhq4);
          } catch {}

          return getQuestionnaireForInvite(x.id, fhq4);
        })
      );
      const questionnairesComparers = uniq((questionnaires).map((x) => `${x.questionnaireId}_${x.fhq4}`));

      if (questionnairesComparers.length === 0 || questionnairesComparers.length > 1) {
        showErrorMessage(I18n.t('differentQuestionnairesError'));

        return;
      }
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to check questionnaires'));
    }

    const hasAnyLocked = selectedItems.some((x) => x.locked === 'true');

    if (hasAnyLocked) {
      showErrorMessage(I18n.t('invitesAreLockedToSendReminder'));

      return;
    }

    const questionnaire = questionnaires[0]!;

    setBatchSendReminderProps({ questionnaire });
  }, [selectedItems, showErrorMessage, getQuestionnaireForInvite]);

  const closeBatchSendReminder = React.useCallback(() => {
    setBatchSendReminderProps(undefined);
  }, []);

  const handleBatchSendReminderSend = async (newInvite: SendInviteParams) => {
    await validateSelectedItemsEmail(newInvite);
    await sendInvites(selectedItems.map(({ globalId }) => {
      const copy = { ...newInvite };
      copy.reminder = true;
      copy.globalId = globalId;

      return copy;
    }));
    closeBatchSendReminder();
    showInfoMessage(I18n.t('The Questionnaire reminder emails were successfully sent'))
  };

  const getBatchUpdateStatusOptions = () => {
    return [
      { label: 'Completed Manually', onClick: () => handleUpdateInvitesStatus(selectedItems, InviteStatus.completeManually) },
      { label: 'Inactive', onClick: () => handleUpdateInvitesStatus(selectedItems, InviteStatus.inactive) },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  };

  const getActions = (invite: InviteListDto) => {
    if ((selectedItems.length || 0) > 1) {
      return [
        { 
          label: (
            <ActionsButton
              className="InviteStatusInvitesTable__update-status__btn"
              withBackdrop={false}
              classes={{
                menuContainer: 'InviteStatusInvitesTable__update-status__menu'
              }}
              id={getTestAttributes(MODULE_NAME, 'update-status', 'dropdown').id}
              triggerType={TRIGGER_TYPE.hover}
              trigger={<React.Fragment>{I18n.t('Update Status')} <span style={{ marginLeft: 8 }} className="fa fa-angle-right" /></React.Fragment>}
            >
              {getBatchUpdateStatusOptions().map((x) => (
                <div key={x.label} className="InviteStatusInvitesTable__update-status__menu__item" onClick={x.onClick}>{x.label}</div>
              ))}
            </ActionsButton>
          ),
        },
        { label: 'Batch Letters', onClick: handleBatchLetters },
        { label: 'New Invites', onClick: handleBatchNewInvitesOpen },
        { label: 'Send Reminders', onClick: handleBatchSendReminderOpen },
        { label: 'Unlock Invites', onClick: handleUnlockInvites },
        { label: 'Lock Invites', onClick: handleLockInvites },
      ].map((x) => ({ ...x, label: typeof x.label === 'string' ? I18n.t(x.label) : x.label }));
    }

    return [
      { label: 'New Invite', onClick: handleNewInviteOpen },
      { label: "Launch New Questionnaire", onClick: handleNewQuestionnaireOpen },
      { label: 'Open Questionnaire', onClick: handleOpenQuestionnaireOpen },
      { label: 'Send Reminder', onClick: handleSendReminderOpen },
      { 
        label: (
          <ActionsButton
            className="InviteStatusInvitesTable__update-status__btn"
            withBackdrop={false}
            classes={{
              menuContainer: 'InviteStatusInvitesTable__update-status__menu'
            }}
            id={getTestAttributes(MODULE_NAME, 'update-status', 'dropdown').id}
            triggerType={TRIGGER_TYPE.hover}
            trigger={<React.Fragment>{I18n.t('Update Status')} <span style={{ marginLeft: 8 }} className="fa fa-angle-right" /></React.Fragment>}
          >
            {getUpdateStatusOptions(invite).map((x) => (
              <div key={x.label} className="InviteStatusInvitesTable__update-status__menu__item" onClick={x.onClick}>{x.label}</div>
            ))}
          </ActionsButton>
        ),
      },
      { label: invite.locked === "true" ? 'Unlock Invite' : 'Lock Invite', onClick: handleLockAndUnlockInvite },
      { label: 'View Invite History', onClick: openViewInviteHistoryModal },
      { label: 'Open Pedigree', onClick: handleOpenPedigree, condition: invite.individualType != "true" },
      { label: 'Open Datasheet', onClick: handleOpenDatasheet },
      { label: 'Generate Letter', onClick: handleGenerateLetter },
    ].filter((x) => x.condition ?? true).map((x) => ({ ...x, label: typeof x.label === 'string' ? I18n.t(x.label) : x.label }));
  };

  const renderActionsButton = React.useCallback(({ row: { original } }: { row: { original: InviteListDto } }) => {
    return (
      <ActionsButton
        id={original.id} 
        stopPropagationOnRoot
        data={original} 
        menuItems={getActions(original)}
        setCurrent={handleSetCurrentSelectedRow}
      />
    );
  }, [getActions, handleSetCurrentSelectedRow]);

  // TODO check all ids

  const tableColumns = React.useMemo((): Array<NewTableColumn<InviteListDto>> => {
    const defaultWidth = 100;

    return [
      { 
        disableActions: true,
        enableResizing: false,
        enableSorting: false,
        header: I18n.t('Actions'), 
        accessorKey: 'ACTIONS_COLUMNS_LOCAL',
        id: 'ACTIONS_COLUMNS_LOCAL',
        cell: renderActionsButton,
        minSize: 65,
        size: 65,
        className: 'table-actions-cell'
      },
      ...columns.map((x) => ({
        id: getColumnId(x),
        accessorKey: x.field.id.toString(),
        enableDragAndDrop: true,
        enableSorting: true,
        minSize: 20,
        size: x.width || defaultWidth,
        header: x.field.name,
        filter: { 
          type: x.field.type,
          options: x.field.options,
          outputDateFormat: 'YYYY-MM-DD'
        }
      }))
    ];
  }, [columns, getColumnId, renderActionsButton]);

  type TableAction<T> = T & {
    condition?: boolean; 
    adminFunction?: AdminSmartListFunctionType;
  };

  const getHeaderActions = () => {
    return [
      { adminFunction: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: handleRestoreColumnsToDefault,  },
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableHeaderPredefinedActionType.addColumn, className: 'InviteStatusInvitesTable__column-action-top-divider', },
    ].map((x: TableAction<NewTableHeaderAction>) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : undefined;
      }

      return x;
    }).filter((x) => x.condition ?? true);
  };

  const getColumnActions = () => {
    return [
      { type: NewTableColumnPredefinedActionType.filters, condition: Boolean(filters) },
      { adminFunction: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: handleRestoreColumnsToDefault, },
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableColumnPredefinedActionType.addColumn, className: 'InviteStatusInvitesTable__column-action-top-divider' },
      { adminFunction: AdminSmartListFunctionType.deleteColumn, type: NewTableColumnPredefinedActionType.deleteColumn },
      { type: NewTableColumnPredefinedActionType.clearSorting },
    ].map((x: TableAction<NewTableColumnAction>) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : undefined;
      }

      return x;
    }).filter((x) => x.condition ?? true);
  };

  const columnActions = getColumnActions();
  const headerActions = getHeaderActions();

  const getHeaderElementId = React.useCallback(() => {
    return 'ped-home-tab-bpane-inv-tab-list-grid-thead-row0';
  }, []);

  const getHeaderCellElementId = React.useCallback((column: NewTableColumn<InviteListDto>, columnIndex: number) => {
    return `ped-home-tab-bpane-inv-tab-list-grid-thead-row0-cell${columnIndex}`;
  }, []);

  const getBodyRowElementId = React.useCallback((dataItem: InviteListDto, rowIndex: number) => {
    return `ped-home-tab-bpane-inv-tab-list-grid-tbody-row${rowIndex}`;
  }, []);

  const getBodyCellElementId = React.useCallback((column: NewTableColumn<InviteListDto>, columnIndex: number, dataItem: InviteListDto, rowIndex: number) => {
    return `ped-home-tab-bpane-inv-tab-list-grid-tbody-row${rowIndex}-cell${columnIndex}`;
  }, []);

  return {
    batchSendReminderProps,
    closeBatchSendReminder,
    handleBatchSendReminderSend,
    closeBatchNewInvites,
    handleBatchNewInvitesSend,
    batchNewInvitesProps,
    getQuestionnairesForBatchInvites,
    handleGetQuestionnairesForNewQuestionnaire,
    closeNewQuestionnaire,
    newQuestionnaireProps,
    handleNewQuestionnaire,
    handleSendReminderSend,
    sendReminderProps,
    closeSendReminder,
    openQuestionnaireProps,
    closeOpenQuestionnaire,
    handleOpenQuestionnaire,
    handleNewInviteSend,
    newInviteProps,
    closeNewInvite,
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    headerActions,
    tableColumns,
    columnActions,
    getData: getDataBase,
    handleColumnsResizing,
    handleColumnsOrderChange,
    getHeaderElementId,
    getHeaderCellElementId,
    getBodyRowElementId,
    getBodyCellElementId,
    viewInviteHistoryProps,
    openViewInviteHistoryModal,
    closeViewInviteHistoryModal,
    updateStatusProps,
    closeUpdateReviewedStatusModal,
    handleUpdateReviewedStatusSubmit
  };
};